import type { Permission } from '@glory/contracts';
import { t } from '@glory/i18n';
import { isValidEmail } from '@glory/mail/domain';
import {
  allowedFor,
  AppShell,
  Dialog,
  type CommandEntry,
  CommandPalette,
  type NavItem,
  UserMenu,
  useMediaQuery,
  useTheme,
} from '@glory/ui';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, redirect, useRouter, useRouterState } from '@tanstack/react-router';
import {
  Bell,
  Banknote,
  Building2,
  BriefcaseBusiness,
  CalendarCheck,
  CalendarDays,
  FileSignature,
  FileText,
  FileUp,
  FolderKanban,
  Handshake,
  Inbox,
  Landmark,
  ListTodo,
  LogOut,
  Mail,
  MessageSquareWarning,
  MessagesSquare,
  Newspaper,
  Package,
  Percent,
  Plus,
  ScrollText,
  Search,
  Settings,
  Shield,
  SlidersHorizontal,
  SquarePen,
  SunMoon,
  UserPlus,
  UserRound,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useClientsPalette } from '../features/clients/runtime/palette.tsx';
import { CURRENT_VERSION } from '../features/news/changelog.ts';
import { MessengerProvider } from '../features/messenger/runtime/context.tsx';
import { CallOverlay } from '../features/messenger/ui/call-slots.tsx';
import { useMessagesBadge } from '../features/messenger/runtime/badge.ts';
import { useMessengerPalette } from '../features/messenger/runtime/palette.tsx';
import { useAiPalette } from '../features/ai/runtime/palette.tsx';
import { useEstimatesPalette } from '../features/estimates/runtime/palette.tsx';
import { useDocumentsPalette } from '../features/documents/runtime/palette.tsx';
import { useProjectsPalette } from '../features/projects/runtime/palette.tsx';
import { TicketDialogHost } from '../features/projects/ui/ticket-dialog.tsx';
import { HeaderTimer, TimerDock } from '../features/projects/ui/timer.tsx';
import { openFeedbackWidget } from '../features/feedback/feedback-widget.tsx';
import { useProposalsPalette } from '../features/proposals/runtime/palette.tsx';
import { useSalesPalette } from '../features/sales/runtime/palette.tsx';
import { CallReturnPrompt } from '../features/sales/ui/call-return.tsx';
import { NotificationsBell } from '../features/notifications/bell.tsx';
import { useSearchResults } from '../features/search/use-search-results.ts';
import { CONTRACT_STEP_PATH, ContractStepBanner } from '../features/team/contract-step.tsx';
import { InviteDialog } from '../features/team/invite-dialog.tsx';
import { type ComposeApi, ComposeProvider, useCompose } from '../features/webmail/compose-provider.tsx';
import { overviewQuery as mailOverviewQuery } from '../features/webmail/lib/queries.ts';
import { totalUnread } from '../features/webmail/lib/scope.ts';
import { authClient } from '../lib/auth-client.ts';
import { LiveProvider } from '../lib/live-provider.tsx';
import { usePermissions } from '../lib/permissions.ts';
import { forgetDeviceBeforeSignOut } from '../lib/push.ts';
import { forgetSections, rememberSection, sectionOf, sectionTarget } from '../lib/section-memory.ts';
import { isUnauthorized, meQuery, resetSession } from '../lib/session.ts';
import { CrumbsProvider, HeaderCrumbs } from '../lib/crumbs.tsx';
import { StudioWorkCalendar } from '../lib/work-calendar.tsx';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.query({ ...meQuery, staleTime: 'static' }).catch((e: unknown) => {
      if (isUnauthorized(e)) throw redirect({ to: '/login', search: { redirect: location.href } });
      throw e;
    });
    // Студия требует защиту входа, а её нет: сначала экран настройки защиты (сервер всё равно закрыт).
    if (me.twoFactorRequired) throw redirect({ to: '/secure', replace: true });
  },
  component: AppLayout,
});

/**
 * Пункты меню: только разделы, которые уже есть. Без `permission` — видны всем. Порядок — по частоте, группы
 * (карта взаимодействия, 4.1): работа, продажи, производство, документы и деньги, студия.
 * На телефоне внизу — самые частые (`mobile`), остальное — в «Ещё».
 */
const NAV: readonly NavItem<Permission>[] = [
  {
    id: 'today',
    label: t('shell.navToday'),
    href: '/today',
    icon: <CalendarCheck />,
    hotkey: 'g t',
    group: 'work',
    mobile: true,
  },
  {
    id: 'messages',
    label: t('shell.navMessages'),
    href: '/messages',
    icon: <MessagesSquare />,
    hotkey: 'g m',
    permission: 'messenger.use',
    group: 'work',
    mobile: true,
  },
  {
    id: 'mail',
    label: t('shell.navMail'),
    href: '/mail',
    icon: <Inbox />,
    hotkey: 'g p',
    permission: 'mail.use',
    group: 'work',
    mobile: true,
  },
  {
    id: 'sales',
    label: t('shell.navSales'),
    href: '/sales',
    icon: <Handshake />,
    hotkey: 'g d',
    permission: 'sales.use',
    group: 'sales',
    mobile: true,
  },
  {
    id: 'clients',
    label: t('shell.navClients'),
    href: '/clients',
    icon: <BriefcaseBusiness />,
    hotkey: 'g l',
    permission: 'sales.use',
    group: 'sales',
    mobile: true,
  },
  {
    id: 'tasks',
    label: t('projects.my.title'),
    href: '/tasks',
    icon: <ListTodo />,
    hotkey: 'g z',
    permission: 'projects.use',
    group: 'production',
  },
  {
    id: 'projects',
    label: t('projects.title'),
    href: '/projects',
    icon: <FolderKanban />,
    hotkey: 'g r',
    permission: 'projects.use',
    group: 'production',
  },
  {
    id: 'documents',
    label: t('documents.nav'),
    href: '/documents',
    icon: <FileText />,
    hotkey: 'g o',
    permission: 'documents.read',
    group: 'money',
  },
  {
    id: 'finance',
    label: t('finance.nav'),
    href: '/finance',
    icon: <Banknote />,
    hotkey: 'g f',
    permission: 'finance.invoices',
    group: 'money',
  },
  {
    id: 'team',
    label: t('shell.navTeam'),
    href: '/team',
    icon: <Users />,
    hotkey: 'g k',
    permission: 'employees.read',
    group: 'studio',
  },
  {
    id: 'catalog',
    label: t('shell.navCatalog'),
    href: '/catalog',
    icon: <Package />,
    hotkey: 'g c',
    permission: 'catalog.read',
    group: 'studio',
  },
  {
    id: 'audit',
    label: t('shell.navAudit'),
    href: '/admin/audit',
    icon: <ScrollText />,
    hotkey: 'g j',
    permission: 'audit.read.all',
    bottom: true,
  },
  {
    id: 'settings',
    label: t('shell.navSettings'),
    href: '/settings',
    icon: <Settings />,
    hotkey: 'g s',
    bottom: true,
  },
  {
    id: 'news',
    label: t('shell.navNews'),
    href: '/news',
    icon: <Newspaper />,
    hotkey: 'g n',
    bottom: true,
    note: CURRENT_VERSION,
  },
];

/** Трёхзонные разделы: список, переписка, панель — на ноутбуке им нужна ширина меню. */
const THREE_ZONE = ['/messages', '/mail'];

/** Общее окно написания доступно командам ⌘K, которые собираются выше провайдера. */
function ComposeBridge({ onApi }: { onApi: (api: ComposeApi) => void }) {
  const api = useCompose();
  useEffect(() => {
    onApi(api);
  }, [api, onApi]);
  return null;
}

function AppLayout() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const { resolved, setPreference } = useTheme();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locationHref = useRouterState({ select: (s) => s.location.href });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [inviting, setInviting] = useState(false);
  const [composeApi, setComposeApi] = useState<ComposeApi | null>(null);
  const search = useSearchResults(query, paletteOpen);
  const typed = query.trim();

  const { can } = usePermissions();
  const messagesBadge = useMessagesBadge(can('messenger.use'));
  const messengerPalette = useMessengerPalette({
    query,
    open: paletteOpen,
    enabled: can('messenger.use'),
    myId: me.id,
    pathname,
  });
  const clientsPalette = useClientsPalette({ query, open: paletteOpen, enabled: can('sales.use') });
  const salesPalette = useSalesPalette({ query, open: paletteOpen, enabled: can('sales.use') });
  const estimatesPalette = useEstimatesPalette({ pathname });
  const proposalsPalette = useProposalsPalette({ pathname });
  const documentsPalette = useDocumentsPalette({ pathname });
  const aiPalette = useAiPalette({ pathname });
  const projectsPalette = useProjectsPalette({ query, open: paletteOpen, enabled: can('projects.use') });
  const mailOverview = useQuery({ ...mailOverviewQuery, enabled: can('mail.use') });
  const mailUnread = totalUnread(mailOverview.data?.accounts ?? []);
  const nav = useMemo(
    () =>
      allowedFor(NAV, can).map((item) => {
        if (item.id === 'messages') return { ...item, count: messagesBadge };
        if (item.id === 'mail') return { ...item, count: mailUnread };
        // На телефоне у сотрудника без продаж «Проекты» — в нижней панели вместо «Продаж» и «Клиентов» (карта, 4.1).
        if (item.id === 'projects') return { ...item, mobile: !can('sales.use') };
        // «Задачи» — туда же (5.2): исполнителю это главный экран дня.
        if (item.id === 'tasks') return { ...item, mobile: !can('sales.use') };
        return item;
      }),
    [can, messagesBadge, mailUnread],
  );
  const active = nav.find((item) => pathname === item.href || pathname.startsWith(`${item.href}/`));
  const activeId = active?.id;
  // В «Сообщениях» и «Почте» на экране уже 1600 px меню само сворачивается до значков (карта, 4.1).
  const wideScreen = useMediaQuery('(min-width: 1600px)');
  const threeZone = THREE_ZONE.some((href) => pathname === href || pathname.startsWith(`${href}/`));

  const go = (href: string) => {
    void router.navigate({ href });
  };
  // Память разделов: где человек был в каждом разделе — туда и ведёт пункт меню. Повторное нажатие на раздел,
  // в котором он уже находится, — в начало раздела.
  const roots = useMemo(() => nav.map((item) => item.href), [nav]);
  useEffect(() => {
    const root = sectionOf(new URL(locationHref, window.location.origin).pathname, roots);
    if (root !== null) rememberSection(root, locationHref);
  }, [locationHref, roots]);
  const goSection = (href: string) => {
    go(active?.href === href ? href : sectionTarget(href));
  };
  const signOut = async () => {
    await forgetDeviceBeforeSignOut();
    forgetSections();
    await authClient.signOut();
    await resetSession(queryClient);
    await router.navigate({ to: '/login', replace: true });
  };

  const entries = allowedFor<CommandEntry<Permission>>(
    [
      ...nav.map((item) => ({
        id: `page:${item.id}`,
        title: item.label,
        group: 'pages' as const,
        icon: item.icon,
        ...(item.hotkey ? { hotkey: item.hotkey } : {}),
        perform: () => {
          goSection(item.href);
        },
      })),
      {
        id: 'page:profile',
        title: t('team.profileTitle'),
        group: 'pages',
        icon: <UserRound />,
        keywords: ['фото', 'аватар', 'профиль', 'о себе'],
        perform: () => {
          go('/profile');
        },
      },
      {
        id: 'page:profile-documents',
        title: t('documents.employee.stepTitle'),
        group: 'pages',
        icon: <FileSignature />,
        keywords: ['договор', 'паспорт', 'личные данные', 'акт о выплате', 'снилс'],
        perform: () => {
          go(CONTRACT_STEP_PATH);
        },
      },
      {
        id: 'page:security',
        title: t('shell.commandSecurity'),
        group: 'pages',
        icon: <Shield />,
        keywords: ['пароль', 'passkey', '2fa', 'сеансы'],
        perform: () => {
          go('/profile/security');
        },
      },
      {
        id: 'page:mail-settings',
        title: t('webmail.settingsLink'),
        group: 'pages',
        icon: <Mail />,
        keywords: ['подпись', 'картинки', 'уведомления', 'почта'],
        permission: 'mail.use',
        perform: () => {
          go('/profile/mail');
        },
      },
      {
        id: 'page:notifications',
        title: t('notifications.settingsTitle'),
        group: 'pages',
        icon: <Bell />,
        keywords: ['push', 'настройки'],
        perform: () => {
          go('/settings/notifications');
        },
      },
      {
        id: 'page:calendar',
        title: t('calendar.title'),
        group: 'pages',
        icon: <CalendarDays />,
        keywords: ['праздники', 'выходные', 'перенос', 'норма часов', 'рабочие дни'],
        perform: () => {
          go('/settings/calendar');
        },
      },
      {
        id: 'page:pricing',
        title: t('pricing.title'),
        group: 'pages',
        icon: <Percent />,
        keywords: ['накладные', 'наценка', 'маржа', 'округление', 'расходы', 'политика цены'],
        permission: 'pricing.read',
        perform: () => {
          go('/settings/pricing');
        },
      },
      {
        id: 'page:tax',
        title: t('tax.title'),
        group: 'pages',
        icon: <Landmark />,
        keywords: ['усн', 'ндс', 'налоговый профиль', 'ставка налога'],
        permission: 'pricing.read',
        perform: () => {
          go('/settings/tax');
        },
      },
      {
        id: 'page:coefficients',
        title: t('coefficients.title'),
        group: 'pages',
        icon: <SlidersHorizontal />,
        keywords: ['срочность', 'надбавка', 'скидка', 'множитель'],
        permission: 'pricing.read',
        perform: () => {
          go('/settings/coefficients');
        },
      },
      {
        id: 'page:studio',
        title: t('studio.title'),
        group: 'pages',
        icon: <Building2 />,
        keywords: ['инн', 'банк', 'бик', 'подписант', 'логотип', 'компания'],
        permission: 'studio.manage',
        perform: () => {
          go('/settings/studio');
        },
      },
      {
        id: 'page:document-settings',
        title: t('documents.settings.pageTitle'),
        group: 'pages',
        icon: <FileText />,
        keywords: ['оферта', 'шаблон', 'договор', 'нумерация', 'подпись', 'текст документа'],
        permission: 'documents.templates',
        perform: () => {
          go('/settings/documents');
        },
      },
      {
        id: 'page:mail',
        title: t('mail.title'),
        group: 'pages',
        icon: <Mail />,
        keywords: ['ящик', 'почтовый', 'dns', 'info@'],
        permission: 'mail.manage',
        perform: () => {
          go('/settings/mail');
        },
      },
      {
        id: 'command:new-item',
        title: t('catalog.newItem'),
        group: 'commands',
        icon: <Plus />,
        keywords: ['услуга', 'шаблон', 'каталог'],
        permission: 'catalog.manage',
        perform: () => {
          void router.navigate({ to: '/catalog', search: { create: true } });
        },
      },
      {
        id: 'command:upload-document',
        title: t('documents.uploadButton'),
        group: 'commands',
        icon: <FileUp />,
        keywords: ['скан', 'документ', 'договор', 'акт', 'загрузить'],
        permission: 'documents.manage',
        perform: () => {
          void router.navigate({ to: '/documents', search: { upload: true } });
        },
      },
      ...salesPalette.commands,
      ...estimatesPalette.commands,
      ...proposalsPalette.commands,
      ...documentsPalette.commands,
      ...aiPalette.commands,
      ...projectsPalette.commands,
      ...clientsPalette.commands,
      ...messengerPalette.commands,
      ...(typed.length >= 2
        ? [
            {
              id: 'command:mail-search',
              title: t('webmail.commandSearch', { query: typed }),
              group: 'commands' as const,
              icon: <Search />,
              permission: 'mail.use',
              perform: () => {
                void router.navigate({ to: '/mail', search: { q: typed } });
              },
            } satisfies CommandEntry<Permission>,
          ]
        : []),
      ...(isValidEmail(typed)
        ? [
            {
              id: 'command:mail-write',
              title: t('webmail.commandWrite', { email: typed }),
              group: 'commands' as const,
              icon: <SquarePen />,
              permission: 'mail.use',
              perform: () => {
                composeApi?.compose({ mode: 'new', to: [{ name: null, email: typed }] });
              },
            } satisfies CommandEntry<Permission>,
          ]
        : []),
      {
        id: 'command:compose',
        title: t('webmail.compose.write'),
        group: 'commands',
        icon: <SquarePen />,
        keywords: ['письмо', 'почта', 'написать', 'email', 'отправить'],
        permission: 'mail.use',
        perform: () => {
          composeApi?.compose();
        },
      },
      {
        id: 'command:invite',
        title: t('team.invite'),
        group: 'commands',
        icon: <UserPlus />,
        keywords: ['сотрудник', 'команда', 'доступ'],
        permission: 'users.invite',
        perform: () => {
          setInviting(true);
        },
      },
      {
        id: 'command:theme',
        title: t('shell.commandTheme'),
        group: 'commands',
        icon: <SunMoon />,
        keywords: ['тёмная', 'светлая'],
        perform: () => {
          setPreference(resolved === 'dark' ? 'light' : 'dark');
        },
      },
      {
        id: 'command:logout',
        title: t('shell.logout'),
        group: 'commands',
        icon: <LogOut />,
        perform: () => void signOut(),
      },
    ],
    can,
  );

  return (
    <LiveProvider>
      <CrumbsProvider>
        <MessengerProvider>
          <ComposeProvider enabled={can('mail.use')}>
            <ComposeBridge onApi={setComposeApi} />
            <AppShell
              nav={nav}
              activeId={activeId}
              renderLink={({ href, children, onClick, ...props }) => (
                <a
                  href={href}
                  {...props}
                  // Наведение на «Сообщения» подгружает код раздела заранее — открывается без ожидания.
                  onPointerEnter={() => {
                    if (href === '/messages') void router.preloadRoute({ to: '/messages' });
                  }}
                  onClick={(event) => {
                    onClick?.();
                    // Открыть в новой вкладке (⌘/Ctrl/Shift, средняя кнопка) — как обычная ссылка.
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                    event.preventDefault();
                    goSection(href);
                  }}
                >
                  {children}
                </a>
              )}
              // Открытый чат (и «Треды») на телефоне занимает весь экран, нижняя панель прячется.
              hideMobileNav={pathname.startsWith('/messages/')}
              compact={threeZone && !wideScreen}
              breadcrumbs={
                <HeaderCrumbs section={active ? { label: active.label, href: active.href } : null} />
              }
              onOpenCommand={() => {
                setPaletteOpen(true);
              }}
              onNavigate={goSection}
              actions={
                <>
                  <HeaderTimer enabled={can('projects.use')} />
                  <NotificationsBell />
                  <UserMenu
                    name={me.name}
                    email={me.email}
                    avatarUrl={me.image}
                    onProfile={() => {
                      go('/profile');
                    }}
                    personal={[
                      {
                        id: 'notifications',
                        label: t('shell.personalNotifications'),
                        icon: <Bell />,
                        onSelect: () => {
                          go('/settings/notifications');
                        },
                      },
                      {
                        id: 'feedback',
                        label: t('feedback.open'),
                        icon: <MessageSquareWarning />,
                        onSelect: openFeedbackWidget,
                      },
                      {
                        id: 'documents',
                        label: t('documents.employee.stepTitle'),
                        icon: <FileSignature />,
                        onSelect: () => {
                          go(CONTRACT_STEP_PATH);
                        },
                      },
                      ...(can('mail.use')
                        ? [
                            {
                              id: 'mail',
                              label: t('shell.personalMail'),
                              icon: <Mail />,
                              onSelect: () => {
                                go('/profile/mail');
                              },
                            },
                          ]
                        : []),
                    ]}
                    onSecurity={() => {
                      go('/profile/security');
                    }}
                    onLogout={() => void signOut()}
                  />
                </>
              }
            >
              <StudioWorkCalendar>
                {/* Договор с сотрудником не подписан — напоминание над содержимым; в трёхзонных разделах не мешаем. */}
                <ContractStepBanner hidden={threeZone || pathname === CONTRACT_STEP_PATH} />
                <Outlet />
                <CallReturnPrompt enabled={can('sales.use')} />
                <TimerDock enabled={can('projects.use')} />
                <CallOverlay />
                <TicketDialogHost
                  enabled={can('sales.use') || can('projects.lead') || can('projects.manage')}
                />
              </StudioWorkCalendar>
              <CommandPalette
                open={paletteOpen}
                onOpenChange={setPaletteOpen}
                entries={entries}
                query={query}
                onQueryChange={setQuery}
                results={[
                  ...salesPalette.results,
                  ...projectsPalette.results,
                  ...clientsPalette.results,
                  ...messengerPalette.results,
                  ...search.results,
                ]}
                searching={search.searching}
              />
              <Dialog open={inviting} onOpenChange={setInviting}>
                {inviting ? (
                  <InviteDialog
                    onDone={() => {
                      setInviting(false);
                    }}
                  />
                ) : null}
              </Dialog>
            </AppShell>
          </ComposeProvider>
        </MessengerProvider>
      </CrumbsProvider>
    </LiveProvider>
  );
}
